import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import useSWR from "swr";
import { authApi, permissionApi, userProfileApi, ApiError } from "@/lib/api";
import {
  isInGracePeriod,
  classifyAuthFailure,
  refreshTokenOnce,
  logoutSession,
  beginContextSwitch,
} from "@/lib/auth-session";
import { connectSocket, disconnectSocket } from "@/lib/socket";

type AuthContextValue = {
  token: string | null;
  selectedSubscriptionId: string | null;
  selectedCompanyId: string | null;
  isSystemMode: boolean;
  isTenantMode: boolean;
  isPlatform: boolean;
  isValidating: boolean;
  isValid: boolean;
  isReady: boolean;
  permissions: string[];
  permissionsLoading: boolean;
  hasPermission: (permission: string | string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  setToken: (token: string | null) => void;
  setSelectedSubscriptionId: (subscriptionId: string | null) => void;
  setSelectedCompanyId: (companyId: string | null) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const PERMISSION_DELIMITER_REGEX = /[.\s]+/g;

const normalizePermissionCode = (permission: string): string =>
  permission.trim().replace(PERMISSION_DELIMITER_REGEX, ".").toUpperCase();

/** Solo si VITE_PERMISSIONS_DEBUG=true (evita ruido y filtrado de PII en consola). */
function authContextDebug(...args: unknown[]): void {
  if (import.meta.env.VITE_PERMISSIONS_DEBUG === "true") {
    console.log(...args);
  }
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const lastTokenRef = useRef<string | null>(null);
  const refreshAttemptedForTokenRef = useRef<string | null>(null);

  const [token, setTokenState] = useState<string | null>(() => {
    try {
      const storedToken = localStorage.getItem("token");
      const timestamp = new Date().toISOString();
      authContextDebug(
        `[AUTH-CONTEXT] ${timestamp} - Inicializando AuthProvider`,
        {
          component: "AuthContext",
          action: "initialization",
          hasStoredToken: !!storedToken,
          tokenLength: storedToken?.length || 0,
          tokenPreview: storedToken
            ? storedToken.substring(0, 20) + "..."
            : "null",
        },
      );
      return storedToken;
    } catch (error) {
      const timestamp = new Date().toISOString();
      console.error(
        `[AUTH-CONTEXT] ${timestamp} - Error inicializando token desde localStorage`,
        {
          component: "AuthContext",
          action: "initializationError",
          error,
        },
      );
      return null;
    }
  });

  const [selectedSubscriptionId, setSubscriptionState] = useState<
    string | null
  >(() => {
    try {
      const stored = localStorage.getItem("selectedSubscriptionId");
      return stored;
    } catch {
      return null;
    }
  });

  const [selectedCompanyId, setCompanyState] = useState<string | null>(() => {
    try {
      const storedCompany = localStorage.getItem("selectedCompanyId");
      return storedCompany;
    } catch {
      return null;
    }
  });

  // Refs para evitar loops infinitos en sincronización con localStorage
  const isUpdatingFromStorageRef = useRef(false);
  // Inicializar refs con valores de localStorage (solo una vez al montar)
  const getInitialToken = (): string | null => {
    try {
      return typeof window !== "undefined"
        ? localStorage.getItem("token")
        : null;
    } catch {
      return null;
    }
  };
  const getInitialSubscriptionId = (): string | null => {
    try {
      return typeof window !== "undefined"
        ? localStorage.getItem("selectedSubscriptionId")
        : null;
    } catch {
      return null;
    }
  };
  const getInitialCompanyId = (): string | null => {
    try {
      return typeof window !== "undefined"
        ? localStorage.getItem("selectedCompanyId")
        : null;
    } catch {
      return null;
    }
  };
  const lastSyncedTokenRef = useRef<string | null>(getInitialToken());
  const lastSyncedSubscriptionIdRef = useRef<string | null>(
    getInitialSubscriptionId(),
  );
  const lastSyncedCompanyIdRef = useRef<string | null>(getInitialCompanyId());

  // Estado de validación - inicializar como true si hay token (para evitar redirección prematura)
  const [isValidState, setIsValidState] = useState<boolean>(() => {
    // Si hay token al inicializar, asumir válido hasta que SWR valide
    const hasToken =
      typeof window !== "undefined" && localStorage.getItem("token");
    return !!hasToken;
  });

  // isReady: false mientras se hace la validación inicial del token en segundo plano.
  // Sin token → listo inmediatamente. Con token → true después del primer intento de validación.
  const [isReady, setIsReady] = useState<boolean>(() => {
    const hasToken =
      typeof window !== "undefined" && localStorage.getItem("token");
    return !hasToken;
  });
  const initialValidationDoneRef = useRef(false);

  // Detectar cuando el token cambia (nuevo login)
  useEffect(() => {
    if (token !== lastTokenRef.current) {
      const wasNull = lastTokenRef.current === null;
      lastTokenRef.current = token;

      if (token && wasNull) {
        // Nuevo login: asumir válido hasta que la validación del backend responda
        setIsValidState(true);
        const timestamp = new Date().toISOString();
        authContextDebug(
          `[AUTH-CONTEXT] ${timestamp} - Nuevo token detectado, validando sesión en backend`,
          {
            component: "AuthContext",
            action: "newToken",
            note: "SWR validates token immediately",
          },
        );
      } else if (!token) {
        setIsValidState(false);
      }
    }
  }, [token]);

  // Conectar/desconectar WebSocket cuando cambia el token
  useEffect(() => {
    if (!token) {
      disconnectSocket();
      return;
    }

    connectSocket(token);

    return () => {
      disconnectSocket();
    };
  }, [token]);

  // SWR maneja toda la validación del token
  // Si existe token, se valida de inmediato contra backend
  const shouldValidate = !!token;

  const {
    data,
    error,
    isValidating: swrValidating,
  } = useSWR(
    shouldValidate ? ["/auth/validate", token] : null,
    async ([, t]) => {
      if (!t) return null;
      authContextDebug(`[AUTH-CONTEXT] SWR validando token`, {
        component: "AuthContext",
        action: "swrValidate",
        hasToken: !!t,
      });
      return authApi.validate();
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: 15 * 60 * 1000,
      revalidateIfStale: false,
      dedupingInterval: 2000,
      shouldRetryOnError: (error) => {
        // No reintentar si es un error de autenticación
        const isAuthError = error?.status === 401 || error?.status === 403;
        return !isAuthError;
      },
      onSuccess: () => {
        const timestamp = new Date().toISOString();
        authContextDebug(
          `[AUTH-CONTEXT] ${timestamp} - SWR validación exitosa`,
          {
            component: "AuthContext",
            action: "swrValidationSuccess",
          },
        );
      },
      onError: (error) => {
        const timestamp = new Date().toISOString();
        const inGrace = isInGracePeriod();
        authContextDebug(
          `[AUTH-CONTEXT] ${timestamp} - SWR error de validación`,
          {
            component: "AuthContext",
            action: "swrValidationError",
            errorStatus: error?.status,
            errorMessage: error?.message,
            inGracePeriod: inGrace,
          },
        );
      },
    },
  );

  // Derivar estado de validación desde SWR
  // Durante período de gracia, mantener isValid si ya estaba validado
  const isValidating = token ? swrValidating : false;

  // Actualizar isValid basado en SWR, respetando período de gracia
  useEffect(() => {
    if (!token) {
      setIsValidState(false);
      if (!initialValidationDoneRef.current) {
        initialValidationDoneRef.current = true;
        setIsReady(true);
      }
      return;
    }

    // Calcular inGracePeriod dentro del efecto para evitar cambios constantes
    const inGracePeriod = isInGracePeriod();

    if (data && !error) {
      // Validación exitosa
      setIsValidState(true);
      if (!initialValidationDoneRef.current) {
        initialValidationDoneRef.current = true;
        setIsReady(true);
      }
    } else if (error) {
      const apiError = error instanceof ApiError ? error : null;
      const status = apiError?.status ?? 0;
      const classification = classifyAuthFailure(
        status,
        apiError?.details,
        "/auth/validate",
      );

      if (
        classification.action === "no_logout" ||
        classification.action === "redirect_subscription" ||
        inGracePeriod
      ) {
        if (!initialValidationDoneRef.current) {
          initialValidationDoneRef.current = true;
          setIsReady(true);
        }
        return;
      }

      const isAuthError = status === 401 || status === 403;
      if (isAuthError) {
        const tryRecoverSession = async () => {
          if (
            token &&
            refreshAttemptedForTokenRef.current !== token &&
            (classification.reason === "token_expired" ||
              classification.reason === "unknown")
          ) {
            refreshAttemptedForTokenRef.current = token;
            const newToken = await refreshTokenOnce(() => authApi.refresh());
            if (newToken) {
              setTokenState(newToken);
              setIsValidState(true);
              if (!initialValidationDoneRef.current) {
                initialValidationDoneRef.current = true;
                setIsReady(true);
              }
              return;
            }
          }

          authContextDebug("[AUTH-CONTEXT] Error de auth, invalidando token", {
            component: "AuthContext",
            action: "invalidateToken",
            errorStatus: status,
            classification,
          });
          setIsValidState(false);
          setToken(null);
        };

        void tryRecoverSession();
      }
      // Marcar primera validación como completada (aunque haya error)
      if (!initialValidationDoneRef.current) {
        initialValidationDoneRef.current = true;
        setIsReady(true);
      }
      // Si estamos en período de gracia o no es error de auth, mantener el estado actual
      // (no actualizar isValidState)
    }
    // Si está validando, mantener el estado actual
  }, [data, error, token]);

  const isValid = isValidState;
  const isSystemMode = selectedSubscriptionId === "SYSTEM";
  const isTenantMode = selectedCompanyId === "TENANT";

  // Cargar permisos del usuario usando SWR
  const shouldLoadPermissions =
    token &&
    (selectedCompanyId || selectedSubscriptionId === "SYSTEM" || isTenantMode);
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useSWR(
    shouldLoadPermissions
      ? ["/auth/permissions", token, selectedCompanyId]
      : null,
    async () => {
      authContextDebug("[AuthContext] Cargando permisos...", {
        hasToken: !!token,
        selectedCompanyId,
      });
      try {
        const result =
          await permissionApi.getUserPermissions(selectedCompanyId);
        authContextDebug("[AuthContext] Permisos recibidos de API:", {
          total: result?.length || 0,
          permissions: result,
        });
        return result;
      } catch (error) {
        console.error("[AuthContext] Error cargando permisos:", error);
        throw error;
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000, // 1 minuto
    },
  );

  // Log de errores de permisos
  React.useEffect(() => {
    if (permissionsError) {
      console.error(
        "[AuthContext] Error en SWR de permisos:",
        permissionsError,
      );
    }
  }, [permissionsError]);

  // Extraer códigos de permisos
  const permissions = useMemo(() => {
    if (!permissionsData) return [];
    const codes = permissionsData.map((p: any) => p.code);
    authContextDebug("[AuthContext] Permisos cargados:", {
      total: codes.length,
      permissions: codes,
      sample: codes.slice(0, 10),
    });
    return codes;
  }, [permissionsData]);

  const normalizedPermissionSet = useMemo(() => {
    return new Set(
      permissions.map((permission) => normalizePermissionCode(permission)),
    );
  }, [permissions]);

  // Cargar info del usuario actual (para isPlatform desde role.isSystem).
  // El endpoint /user/my-info en backend tiene @SkipTenantGuard + @SkipSubtenantGuard,
  // así que solo necesita el token (Authorization). No se traga el error: SWR lo
  // maneja y reintenta con la config global (errorRetryCount:1, errorRetryInterval:5s).
  // revalidateOnFocus:true + dedupingInterval:5s garantizan que al volver a la pestaña
  // o tras navegar se revalide, evitando que un fallo transitorio cachee "null" como
  // data válida y deje isPlatform en false para siempre.
  const { data: myInfo } = useSWR<{
    role?: { isSystem?: boolean };
  } | null>(
    token ? ["/user/my-info", token] : null,
    () => userProfileApi.getMyInfo(),
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    },
  );
  const isPlatform = myInfo?.role?.isSystem === true;

  // Métodos para validar permisos
  const hasPermission = useCallback(
    (permission: string | string[]): boolean => {
      if (Array.isArray(permission)) {
        return permission.some((p) =>
          normalizedPermissionSet.has(normalizePermissionCode(p)),
        );
      }
      return normalizedPermissionSet.has(normalizePermissionCode(permission));
    },
    [normalizedPermissionSet],
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.some((permission) =>
        normalizedPermissionSet.has(normalizePermissionCode(permission)),
      );
    },
    [normalizedPermissionSet],
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.every((permission) =>
        normalizedPermissionSet.has(normalizePermissionCode(permission)),
      );
    },
    [normalizedPermissionSet],
  );

  // Sincronizar con localStorage cuando cambia el token (solo si no viene de storage event)
  useEffect(() => {
    if (isUpdatingFromStorageRef.current) {
      // Si estamos actualizando desde un storage event, no escribir de vuelta
      isUpdatingFromStorageRef.current = false;
      return;
    }

    if (token && token !== lastSyncedTokenRef.current) {
      try {
        const storedToken = localStorage.getItem("token");
        if (storedToken !== token) {
          localStorage.setItem("token", token);
          lastSyncedTokenRef.current = token;
        }
      } catch (error) {
        console.error("[AUTH-CONTEXT] Error sincronizando token:", error);
      }
    } else if (!token && lastSyncedTokenRef.current !== null) {
      // Si el token se eliminó, limpiar localStorage
      try {
        localStorage.removeItem("token");
        lastSyncedTokenRef.current = null;
      } catch (error) {
        console.error("[AUTH-CONTEXT] Error limpiando token:", error);
      }
    }
  }, [token]);

  // Sincronizar con localStorage cuando cambia selectedSubscriptionId (solo si no viene de storage event)
  useEffect(() => {
    if (isUpdatingFromStorageRef.current) {
      return;
    }

    if (
      selectedSubscriptionId &&
      selectedSubscriptionId !== lastSyncedSubscriptionIdRef.current
    ) {
      try {
        const stored = localStorage.getItem("selectedSubscriptionId");
        if (stored !== selectedSubscriptionId) {
          localStorage.setItem(
            "selectedSubscriptionId",
            selectedSubscriptionId,
          );
          lastSyncedSubscriptionIdRef.current = selectedSubscriptionId;
        }
      } catch (error) {
        console.error(
          "[AUTH-CONTEXT] Error sincronizando subscriptionId:",
          error,
        );
      }
    } else if (
      !selectedSubscriptionId &&
      lastSyncedSubscriptionIdRef.current !== null
    ) {
      try {
        localStorage.removeItem("selectedSubscriptionId");
        lastSyncedSubscriptionIdRef.current = null;
      } catch (error) {
        console.error("[AUTH-CONTEXT] Error limpiando subscriptionId:", error);
      }
    }
  }, [selectedSubscriptionId]);

  // Sincronizar con localStorage cuando cambia selectedCompanyId (solo si no viene de storage event)
  useEffect(() => {
    if (isUpdatingFromStorageRef.current) {
      // Si estamos actualizando desde un storage event, no escribir de vuelta
      return;
    }

    if (
      selectedCompanyId &&
      selectedCompanyId !== lastSyncedCompanyIdRef.current
    ) {
      try {
        const storedCompanyId = localStorage.getItem("selectedCompanyId");
        if (storedCompanyId !== selectedCompanyId) {
          localStorage.setItem("selectedCompanyId", selectedCompanyId);
          lastSyncedCompanyIdRef.current = selectedCompanyId;
        }
      } catch (error) {
        console.error("[AUTH-CONTEXT] Error sincronizando companyId:", error);
      }
    } else if (!selectedCompanyId && lastSyncedCompanyIdRef.current !== null) {
      // Si el companyId se eliminó, limpiar localStorage
      try {
        localStorage.removeItem("selectedCompanyId");
        lastSyncedCompanyIdRef.current = null;
      } catch (error) {
        console.error("[AUTH-CONTEXT] Error limpiando companyId:", error);
      }
    }
  }, [selectedCompanyId]);

  // Refs para acceder a valores actuales en storage event handler
  const tokenRef = useRef(token);
  const selectedSubscriptionIdRef = useRef(selectedSubscriptionId);
  const selectedCompanyIdRef = useRef(selectedCompanyId);

  // Mantener refs actualizados
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    selectedSubscriptionIdRef.current = selectedSubscriptionId;
  }, [selectedSubscriptionId]);

  useEffect(() => {
    selectedCompanyIdRef.current = selectedCompanyId;
  }, [selectedCompanyId]);

  // Efecto para sincronizar con localStorage (storage events de otras pestañas)
  // IMPORTANTE: Solo escuchar eventos de otras pestañas, no de la misma
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Solo procesar eventos de otras pestañas (storage events no se disparan en la misma pestaña)
      if (e.key === "token" && e.newValue !== tokenRef.current) {
        isUpdatingFromStorageRef.current = true;
        setTokenState(e.newValue);
        lastSyncedTokenRef.current = e.newValue;
        tokenRef.current = e.newValue;
      } else if (
        e.key === "selectedSubscriptionId" &&
        e.newValue !== selectedSubscriptionIdRef.current
      ) {
        isUpdatingFromStorageRef.current = true;
        setSubscriptionState(e.newValue);
        lastSyncedSubscriptionIdRef.current = e.newValue;
        selectedSubscriptionIdRef.current = e.newValue;
      } else if (
        e.key === "selectedCompanyId" &&
        e.newValue !== selectedCompanyIdRef.current
      ) {
        isUpdatingFromStorageRef.current = true;
        setCompanyState(e.newValue);
        lastSyncedCompanyIdRef.current = e.newValue;
        selectedCompanyIdRef.current = e.newValue;
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const setToken = useCallback(
    (next: string | null) => {
      // Evitar cambios innecesarios
      if (next === token) {
        return;
      }

      const timestamp = new Date().toISOString();
      const hasToken = !!next;

      authContextDebug(`[AUTH-CONTEXT] ${timestamp} - setToken called`, {
        component: "AuthContext",
        action: "setToken",
        hasToken,
        tokenLength: next?.length || 0,
        previousTokenExists: !!token,
        currentPath:
          typeof window !== "undefined" ? window.location.pathname : "unknown",
      });

      // Guardar inmediatamente en localStorage para evitar race conditions
      try {
        if (next) {
          localStorage.setItem("token", next);
          lastSyncedTokenRef.current = next;
        } else {
          localStorage.removeItem("token");
          lastSyncedTokenRef.current = null;
        }
        authContextDebug(
          `[AUTH-CONTEXT] ${timestamp} - Token guardado inmediatamente en localStorage`,
          {
            component: "AuthContext",
            action: "tokenSavedToLocalStorage",
            hasToken: !!next,
          },
        );
      } catch (error) {
        console.error(
          `[AUTH-CONTEXT] ${timestamp} - Error guardando token en localStorage:`,
          error,
        );
      }

      // Actualizar estado - los useEffect de sincronización se encargarán de mantener consistencia
      setTokenState(next);
      tokenRef.current = next;
    },
    [token],
  );

  const setSelectedSubscriptionId = useCallback(
    (next: string | null) => {
      if (next === selectedSubscriptionId) {
        return;
      }

      beginContextSwitch();

      try {
        if (next) {
          localStorage.setItem("selectedSubscriptionId", next);
          lastSyncedSubscriptionIdRef.current = next;
        } else {
          localStorage.removeItem("selectedSubscriptionId");
          lastSyncedSubscriptionIdRef.current = null;
        }
      } catch (error) {
        console.error(
          "[AUTH-CONTEXT] Error guardando subscriptionId en localStorage:",
          error,
        );
      }

      setSubscriptionState(next);
      selectedSubscriptionIdRef.current = next;
    },
    [selectedSubscriptionId],
  );

  const setSelectedCompanyId = useCallback(
    (next: string | null) => {
      if (next === selectedCompanyId) {
        return;
      }

      beginContextSwitch();

      const timestamp = new Date().toISOString();

      authContextDebug(
        `[AUTH-CONTEXT] ${timestamp} - setSelectedCompanyId called`,
        {
          component: "AuthContext",
          action: "setSelectedCompanyId",
          companyId: next,
          previousCompanyId: selectedCompanyId,
          hasToken: !!token,
          currentPath:
            typeof window !== "undefined"
              ? window.location.pathname
              : "unknown",
        },
      );

      // Guardar inmediatamente en localStorage para evitar race conditions
      try {
        if (next) {
          localStorage.setItem("selectedCompanyId", next);
          lastSyncedCompanyIdRef.current = next;
        } else {
          localStorage.removeItem("selectedCompanyId");
          lastSyncedCompanyIdRef.current = null;
        }
        authContextDebug(
          `[AUTH-CONTEXT] ${timestamp} - CompanyId guardado inmediatamente en localStorage`,
          {
            component: "AuthContext",
            action: "companyIdSavedToLocalStorage",
            hasCompanyId: !!next,
          },
        );
      } catch (error) {
        console.error(
          `[AUTH-CONTEXT] ${timestamp} - Error guardando companyId en localStorage:`,
          error,
        );
      }

      // Actualizar estado - los useEffect de sincronización se encargarán de mantener consistencia
      setCompanyState(next);
      selectedCompanyIdRef.current = next;
    },
    [selectedCompanyId, token],
  );

  const logout = useCallback(() => {
    authContextDebug("[AUTH-CONTEXT] Ejecutando logout completo", {
      component: "AuthContext",
      action: "logout",
      hadToken: !!token,
    });

    disconnectSocket();
    setTokenState(null);
    setSubscriptionState(null);
    setCompanyState(null);
    logoutSession();
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      selectedSubscriptionId,
      selectedCompanyId,
      isSystemMode,
      isTenantMode,
      isPlatform,
      isValidating,
      isValid,
      isReady,
      permissions,
      permissionsLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      setToken,
      setSelectedSubscriptionId,
      setSelectedCompanyId,
      logout,
    }),
    [
      token,
      selectedSubscriptionId,
      selectedCompanyId,
      isSystemMode,
      isTenantMode,
      isPlatform,
      isValidating,
      isValid,
      isReady,
      permissions,
      permissionsLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      setToken,
      setSelectedSubscriptionId,
      setSelectedCompanyId,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
